import { AccordionDetails, AccordionSummary, Button, Snackbar, Stack, Typography } from "~/common/mui";
import { SnackbarCloseButton } from "~/common/mui/Snackbar/Snackbar";
import { useTranslation } from "react-i18next";
import { ServiceCasesTableV2 } from "~/features/orderbook/components/ServiceCreationFlow/ServiceCasesTableV2";
import {
  NoServiceCaseContainer,
  NoServiceCaseImage,
  NoServiceCaseImageContainer,
  OrderLineServiceCase,
} from "../common/components/v2/OrderAndQuoteSharedStyles";
import { IMG_NO_RESULTS_OR_MATCHES } from "~/utils/url/staticResources";
import type { CaseDataObject } from "../orderDetails/components/ServiceCases/types";
import { Permission, userHasPermission } from "~/domains/user/utils/permissions";

interface Props {
  serviceCases: CaseDataObject[];
  userCreateCase: boolean;
  open: () => void;
  lineNo?: string;
  soNumber?: string;
  snackbarData: {
    open: boolean;
    message: string;
  };
  setSnackbarData: React.Dispatch<
    React.SetStateAction<{
      open: boolean;
      message: string;
    }>
  >;
  popups: React.ReactNode;
  downloadPopup?: React.ReactNode;
}

const ServiceCasesSection = ({
  serviceCases,
  userCreateCase,
  open,
  lineNo,
  soNumber,
  snackbarData,
  setSnackbarData,
  popups,
}: Props) => {
  const { t } = useTranslation();
  const contactSchaefflerPermission = userHasPermission(Permission.ORDER_CONTACT);
  const serviceCaselength = serviceCases.filter((item) => item.completeData).length;
  const serviceCaselengthText = serviceCaselength > 0 ? `(${serviceCaselength})` : "";

  const renderServiceCaseList = () => {
    if (serviceCases.length === 0 && !userCreateCase) {
      return (
        <NoServiceCaseContainer>
          <NoServiceCaseImageContainer>
            <NoServiceCaseImage src={IMG_NO_RESULTS_OR_MATCHES} alt={t("serviceCases.list.dialog.noCaseFound.alt")} />
          </NoServiceCaseImageContainer>
          <Typography variant="bodyLarge">{t("orderDetails.service.case.order.line.text")}</Typography>
        </NoServiceCaseContainer>
      );
    }

    return (
      <ServiceCasesTableV2
        cases={serviceCases}
        lineNumber={lineNo}
        orderNumber={soNumber}
        userHasCreatedCase={userCreateCase}
        setSnackbar={setSnackbarData}
      />
    );
  };

  return (
    <OrderLineServiceCase>
      <AccordionSummary>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ width: "100%" }}>
          <Typography variant="bodyLarge">{`${t(
            "page.abmonitor.tableRow.serviceCases",
          )} ${serviceCaselengthText}`}</Typography>

          {contactSchaefflerPermission && (
            <Button
              size="medium"
              variant="text"
              onClick={(event) => {
                event.stopPropagation();
                open();
              }}
              onFocus={(event) => event.stopPropagation()}
            >
              {t("orderList.orderLineListRow.createService")}
            </Button>
          )}
        </Stack>
      </AccordionSummary>

      <AccordionDetails>
        {renderServiceCaseList()}

        {contactSchaefflerPermission && (
          <Snackbar
            onClose={() =>
              setSnackbarData((prev) => ({
                ...prev,
                open: false,
              }))
            }
            open={snackbarData.open}
            autoHideDuration={7000}
            transitionDuration={{
              exit: 500,
              appear: 500,
              enter: 500,
            }}
            message={snackbarData.message}
            action={
              <SnackbarCloseButton
                onClick={() =>
                  setSnackbarData((prev) => ({
                    ...prev,
                    open: false,
                  }))
                }
              />
            }
          />
        )}
      </AccordionDetails>

      {popups}
    </OrderLineServiceCase>
  );
};

export default ServiceCasesSection;
